import { remarksSchema } from './campaign';
import {
  endsAtSchema,
  lastUpdatedAtSchema,
  regexSchema,
  startsAtSchema,
} from './company';
import { halfWidthNumber } from './regex';

import { TerminalTypeKey } from '@/types';
import {
  InferType,
  boolean,
  object,
  string,
} from '@/validations/custom-yup';

/** 外部端末ID */
export const externalTerminalIdSchema = string()
  .label('外部端末ID')
  .notRequired()
  .max(100)
  .when(
    'regex',
    ([regex], schema) => (regex ? schema.matches(regex, '外部端末IDのフォーマットを確認してください。') : schema),
  );

/** 端末名 */
export const terminalNameSchema = string()
  .label('端末名')
  .notRequired()
  .max(100, '100桁以内で入力してください。');

/** 端末種別 */
export const terminalTypeSchema = string<TerminalTypeKey>()
  .label('端末種別')
  .required(({ label }) => `${label}を選択してください。`);

/** 端末パスワード */
export const terminalPasswordSchema = string()
  .label('端末パスワード')
  .when('canEditTerminalPassword', {
    is: true,
    then: (it) => it
      .length(4, '端末パスワードは4桁で入力してください。')
      .matches(halfWidthNumber, '端末パスワードは半角数字で入力してください。')
      .required(),
    otherwise: (it) => it.notRequired(),
  });

/** 店舗ID */
export const shopIdSchema = string()
  .label('店舗ID');

/** 端末ステッカーID */
export const terminalStickerIdSchema = string()
  .label('端末ステッカーID')
  .notRequired()
  .test(
    'isLength6',
    '端末ステッカーIDは6文字で入力してください。',
    (value) => (value ? value.length === 6 : true),
  );

/** 端末パスワードを編集可能かどうか */
export const canEditTerminalPasswordSchema = boolean()
  .defined()
  .default(false);

export const terminalCustomField1Schema = string()
  .label('端末自由項目1')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'terminalCustomField1Regex',
    ([terminalCustomField1Regex], schema) => (terminalCustomField1Regex ? schema.matches(terminalCustomField1Regex, '端末自由項目１のフォーマットを確認してください。') : schema),
  );

export const terminalCustomField2Schema = string()
  .label('端末自由項目2')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'terminalCustomField2Regex',
    ([terminalCustomField2Regex], schema) => (terminalCustomField2Regex ? schema.matches(terminalCustomField2Regex, '端末自由項目２のフォーマットを確認してください。') : schema),
  );

export const terminalCustomField3Schema = string()
  .label('端末自由項目3')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'terminalCustomField3Regex',
    ([terminalCustomField3Regex], schema) => (terminalCustomField3Regex ? schema.matches(terminalCustomField3Regex, '端末自由項目３のフォーマットを確認してください。') : schema),
  );

export const terminalCustomField4Schema = string()
  .label('端末自由項目4')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'terminalCustomField4Regex',
    ([terminalCustomField4Regex], schema) => (terminalCustomField4Regex ? schema.matches(terminalCustomField4Regex, '端末自由項目４のフォーマットを確認してください。') : schema),
  );

export const terminalCustomField5Schema = string()
  .label('端末自由項目5')
  .max(100, '100桁以内で入力してください。')
  .notRequired()
  .when(
    'terminalCustomField5Regex',
    ([terminalCustomField5Regex], schema) => (terminalCustomField5Regex ? schema.matches(terminalCustomField5Regex, '端末自由項目５のフォーマットを確認してください。') : schema),
  );

export const terminalCustomField1RegexSchema = string()
  .label('端末自由項目１正規表現');

export const terminalCustomField2RegexSchema = string()
  .label('端末自由項目２正規表現');

export const terminalCustomField3RegexSchema = string()
  .label('端末自由項目３正規表現');

export const terminalCustomField4RegexSchema = string()
  .label('端末自由項目４正規表現');

export const terminalCustomField5RegexSchema = string()
  .label('端末自由項目５正規表現');

export const terminalEditFormSchema = object({
  externalId: externalTerminalIdSchema,
  terminalType: terminalTypeSchema,
  terminalPassword: terminalPasswordSchema,
  shopId: shopIdSchema,
  startsAt: startsAtSchema,
  endsAt: endsAtSchema,
  terminalStickerId: terminalStickerIdSchema,
  remarks: remarksSchema,
  regex: regexSchema,
  lastUpdatedAt: lastUpdatedAtSchema,
  canEditTerminalPassword: canEditTerminalPasswordSchema,
  terminalName: terminalNameSchema,
  terminalCustomField1: terminalCustomField1Schema,
  terminalCustomField2: terminalCustomField2Schema,
  terminalCustomField3: terminalCustomField3Schema,
  terminalCustomField4: terminalCustomField4Schema,
  terminalCustomField5: terminalCustomField5Schema,
  terminalCustomField1Regex: terminalCustomField1RegexSchema,
  terminalCustomField2Regex: terminalCustomField2RegexSchema,
  terminalCustomField3Regex: terminalCustomField3RegexSchema,
  terminalCustomField4Regex: terminalCustomField4RegexSchema,
  terminalCustomField5Regex: terminalCustomField5RegexSchema,
});

export type TerminalEditForm = InferType<typeof terminalEditFormSchema>;
